/**
 * UI Store - Panel state management
 *
 * BYK-160: Dedicated Zustand store for UI panel state
 *
 * Manages:
 * - Panel collapse/expand states
 * - Panel width preferences
 * - Active section tracking
 * - Layout presets
 * - Keyboard shortcut triggers
 */

import { create } from "zustand";
import { subscribeWithSelector, persist } from "zustand/middleware";

// =============================================================================
// Types
// =============================================================================

/** Active section within left sidebar */
export type LeftSidebarSection = "products" | "layouts" | "styles";

/** Active section within right sidebar */
export type RightSidebarSection = "properties" | "ai" | "render" | "issues" | "quote";

/** Layout preset configurations */
export type LayoutPreset =
  | "full-canvas"      // Both sidebars collapsed
  | "design-mode"      // Left open, right collapsed
  | "properties-mode"  // Right open, left collapsed
  | "split-view"       // Both sidebars open (default)
  | "ai-focus";        // Right sidebar expanded with AI

/** Panel size constraints (percentages) */
export interface PanelSizeConstraints {
  min: number;
  max: number;
  default: number;
}

// =============================================================================
// Constants
// =============================================================================

export const LEFT_PANEL_CONSTRAINTS: PanelSizeConstraints = {
  min: 15,
  max: 30,
  default: 20,
};

export const RIGHT_PANEL_CONSTRAINTS: PanelSizeConstraints = {
  min: 18,
  max: 35,
  default: 22,
};

/** Preset configurations */
export const LAYOUT_PRESETS: Record<LayoutPreset, {
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  leftWidth: number;
  rightWidth: number;
  rightSection?: RightSidebarSection;
}> = {
  "full-canvas": {
    leftCollapsed: true,
    rightCollapsed: true,
    leftWidth: LEFT_PANEL_CONSTRAINTS.default,
    rightWidth: RIGHT_PANEL_CONSTRAINTS.default,
  },
  "design-mode": {
    leftCollapsed: false,
    rightCollapsed: true,
    leftWidth: LEFT_PANEL_CONSTRAINTS.default,
    rightWidth: RIGHT_PANEL_CONSTRAINTS.default,
  },
  "properties-mode": {
    leftCollapsed: true,
    rightCollapsed: false,
    leftWidth: LEFT_PANEL_CONSTRAINTS.default,
    rightWidth: RIGHT_PANEL_CONSTRAINTS.default,
  },
  "split-view": {
    leftCollapsed: false,
    rightCollapsed: false,
    leftWidth: LEFT_PANEL_CONSTRAINTS.default,
    rightWidth: RIGHT_PANEL_CONSTRAINTS.default,
  },
  "ai-focus": {
    leftCollapsed: true,
    rightCollapsed: false,
    leftWidth: LEFT_PANEL_CONSTRAINTS.default,
    rightWidth: 30, // Larger for AI chat
    rightSection: "ai",
  },
};

// =============================================================================
// Store Interface
// =============================================================================

interface UIState {
  // Panel collapse states
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;

  // Panel width states (percentages)
  leftSidebarWidth: number;
  rightSidebarWidth: number;

  // Active section within each sidebar
  activeLeftSection: LeftSidebarSection;
  activeRightSection: RightSidebarSection;

  // Current layout preset (if using one)
  activePreset: LayoutPreset | null;

  // Focus management
  focusedPanel: "left" | "center" | "right";

  // Actions - Panel collapse/expand
  setLeftCollapsed: (collapsed: boolean) => void;
  setRightCollapsed: (collapsed: boolean) => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;

  // Actions - Panel width
  setLeftWidth: (width: number) => void;
  setRightWidth: (width: number) => void;

  // Actions - Active sections
  setActiveLeftSection: (section: LeftSidebarSection) => void;
  setActiveRightSection: (section: RightSidebarSection) => void;

  // Actions - Layout presets
  applyPreset: (preset: LayoutPreset) => void;
  getCurrentPreset: () => LayoutPreset | null;

  // Actions - Focus management
  setFocusedPanel: (panel: "left" | "center" | "right") => void;
  focusCanvas: () => void;

  // Actions - Utility
  resetToDefault: () => void;
}

// =============================================================================
// Store Implementation
// =============================================================================

export const useUIStore = create<UIState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        leftSidebarCollapsed: false,
        rightSidebarCollapsed: false,
        leftSidebarWidth: LEFT_PANEL_CONSTRAINTS.default,
        rightSidebarWidth: RIGHT_PANEL_CONSTRAINTS.default,
        activeLeftSection: "products",
        activeRightSection: "properties",
        activePreset: "split-view",
        focusedPanel: "center",

        // Actions - Panel collapse/expand
        setLeftCollapsed: (collapsed) => {
          set({ leftSidebarCollapsed: collapsed, activePreset: null });
        },

        setRightCollapsed: (collapsed) => {
          set({ rightSidebarCollapsed: collapsed, activePreset: null });
        },

        toggleLeftSidebar: () => {
          set((state) => ({
            leftSidebarCollapsed: !state.leftSidebarCollapsed,
            activePreset: null, // Clear preset when manually toggling
          }));
        },

        toggleRightSidebar: () => {
          set((state) => ({
            rightSidebarCollapsed: !state.rightSidebarCollapsed,
            activePreset: null, // Clear preset when manually toggling
          }));
        },

        // Actions - Panel width
        setLeftWidth: (width) => {
          const clamped = Math.max(
            LEFT_PANEL_CONSTRAINTS.min,
            Math.min(LEFT_PANEL_CONSTRAINTS.max, width)
          );
          set({ leftSidebarWidth: clamped, activePreset: null });
        },

        setRightWidth: (width) => {
          const clamped = Math.max(
            RIGHT_PANEL_CONSTRAINTS.min,
            Math.min(RIGHT_PANEL_CONSTRAINTS.max, width)
          );
          set({ rightSidebarWidth: clamped, activePreset: null });
        },

        // Actions - Active sections
        setActiveLeftSection: (section) => {
          set({
            activeLeftSection: section,
            // Auto-expand if collapsed
            leftSidebarCollapsed: false,
          });
        },

        setActiveRightSection: (section) => {
          set({
            activeRightSection: section,
            // Auto-expand if collapsed
            rightSidebarCollapsed: false,
          });
        },

        // Actions - Layout presets
        applyPreset: (preset) => {
          const config = LAYOUT_PRESETS[preset];
          set({
            leftSidebarCollapsed: config.leftCollapsed,
            rightSidebarCollapsed: config.rightCollapsed,
            leftSidebarWidth: config.leftWidth,
            rightSidebarWidth: config.rightWidth,
            activePreset: preset,
            ...(config.rightSection && { activeRightSection: config.rightSection }),
          });
        },

        getCurrentPreset: () => {
          const state = get();
          // Check if current state matches any preset
          for (const [name, config] of Object.entries(LAYOUT_PRESETS)) {
            if (
              state.leftSidebarCollapsed === config.leftCollapsed &&
              state.rightSidebarCollapsed === config.rightCollapsed &&
              Math.abs(state.leftSidebarWidth - config.leftWidth) < 2 &&
              Math.abs(state.rightSidebarWidth - config.rightWidth) < 2
            ) {
              return name as LayoutPreset;
            }
          }
          return null;
        },

        // Actions - Focus management
        setFocusedPanel: (panel) => {
          set({ focusedPanel: panel });
        },

        focusCanvas: () => {
          set({ focusedPanel: "center" });
        },

        // Actions - Utility
        resetToDefault: () => {
          const defaultPreset = LAYOUT_PRESETS["split-view"];
          set({
            leftSidebarCollapsed: defaultPreset.leftCollapsed,
            rightSidebarCollapsed: defaultPreset.rightCollapsed,
            leftSidebarWidth: defaultPreset.leftWidth,
            rightSidebarWidth: defaultPreset.rightWidth,
            activeLeftSection: "products",
            activeRightSection: "properties",
            activePreset: "split-view",
            focusedPanel: "center",
          });
        },
      }),
      {
        name: "byk-ui-state",
        version: 1,
        partialize: (state) => ({
          leftSidebarCollapsed: state.leftSidebarCollapsed,
          rightSidebarCollapsed: state.rightSidebarCollapsed,
          leftSidebarWidth: state.leftSidebarWidth,
          rightSidebarWidth: state.rightSidebarWidth,
          activeLeftSection: state.activeLeftSection,
          activeRightSection: state.activeRightSection,
          activePreset: state.activePreset,
        }),
      }
    )
  )
);

// =============================================================================
// Selectors (for performance optimization)
// =============================================================================

/** Select only left sidebar state */
export const selectLeftSidebar = (state: UIState) => ({
  collapsed: state.leftSidebarCollapsed,
  width: state.leftSidebarWidth,
  activeSection: state.activeLeftSection,
});

/** Select only right sidebar state */
export const selectRightSidebar = (state: UIState) => ({
  collapsed: state.rightSidebarCollapsed,
  width: state.rightSidebarWidth,
  activeSection: state.activeRightSection,
});

/** Select panel focus state */
export const selectPanelFocus = (state: UIState) => ({
  focusedPanel: state.focusedPanel,
  isLeftFocused: state.focusedPanel === "left",
  isCenterFocused: state.focusedPanel === "center",
  isRightFocused: state.focusedPanel === "right",
});

export default useUIStore;
